import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  UiTextField,
  UiLabel,
  UiFormControlLabel,
  UiRadioGroup,
  UiRadio,
  UiCheckbox,
} from 'ui-components';
import {
  commaSeperator,
  magicNumbers,
  isMatchWithExcelTitleConditions,
  isNumber,
} from 'utils';
import { changeExportFormats, changeExportvalue } from 'store';
import RingChart from 'components/visualize/ring-chart/RingChart';
import Loading from 'components/loading/Loading';
import { usePermission } from 'hooks';
import { StyledExportContent } from '../ExportContent.styles';

const ExportContentView = ({ submit, randomSort }) => {
  const dispatch = useDispatch();
  const { userPermission } = usePermission();
  const { t } = useTranslation();

  const limits = useSelector((state) => state.excelExportReducer.limits);
  const exportFormats = useSelector(
    (state) => state.excelExportReducer.exportFormats
  );
  const isRandomSortEnabled = useSelector(
    (state) => state.excelExportReducer.isRandomSortEnabled
  );
  const title = useSelector((state) => state.excelExportReducer.title);
  const count = useSelector((state) => state.excelExportReducer.count);
  const loading = useSelector(
    (state) => state.excelExportReducer.export.loading
  );

  // پرمیشن جهت دریافت محدودیت ردیف های اکسل
  const { int_value_1: permi_excel_resource_count } = useMemo(
    () => userPermission('Variables', 'search_excel_post_count'),
    [userPermission]
  );

  if (limits.loading) {
    return <Loading spinning />;
  }

  const chartTooltips = [
    {
      type: t('settings:excelRemainingVolume'),
      percent: +((limits?.data?.remaining * 100) / limits?.data?.total).toFixed(
        0
      ),
    },
    {
      type: t('settings:excelConsumedVolume'),
      percent:
        100 -
        +((limits?.data?.remaining * 100) / limits?.data?.total).toFixed(0),
    },
  ];

  const chartContent = {
    percent: `${((limits?.data?.remaining * 100) / limits?.data?.total).toFixed(
      0
    )}%`,
  };

  const maximumExcelRecord = () => {
    return Math.min(
      permi_excel_resource_count || magicNumbers.MAX_ROWS_FOR_EXPORT_EXCEL,
      magicNumbers.MAX_ROWS_FOR_EXPORT_EXCEL
    );
  };

  /**
   * تغییر مقدار تعداد ردیف‌ها
   */
  const changeValue = (e) => {
    // desc: اگر اولین عدد صفر باشد نباید درج شود
    if (e.target.value?.indexOf(0) == 0) return null;

    // درهرصورت ماکسیمم ردیفهایی که در هر بار میتوان خروجی گرفت نباید بیشتر از MAX_ROWS_FOR_EXPORT_EXCEL باشد
    const _remainingCount =
      limits?.data?.remaining > maximumExcelRecord()
        ? maximumExcelRecord()
        : limits?.data?.remaining;

    if (
      !e.target.value ||
      (isNumber(e.target.value) && e.target.value <= _remainingCount)
    ) {
      dispatch(
        changeExportvalue({
          key: 'count',
          value: e.target.value,
        })
      );
    }
  };

  const changeFormat = (e) => {
    dispatch(changeExportFormats(e.target.value));
  };

  const changeTitle = (e) => {
    if (
      isMatchWithExcelTitleConditions(e.target.value) &&
      e.target.value.length <= magicNumbers.EXPORT_EXCEL_MAX_TITLE_LENGTH
    )
      dispatch(
        changeExportvalue({
          key: 'title',
          value: e.target.value,
        })
      );
  };

  const changeSortValue = () => {
    dispatch(
      changeExportvalue({
        key: 'isRandomSortEnabled',
        value: !isRandomSortEnabled,
      })
    );
  };

  const handleEnter = (event) => {
    if (event.key === 'Enter') {
      if (!loading && count > 0 && limits?.data?.remaining >= count) submit();
    }
  };

  return (
    <StyledExportContent className='flex between align-start'>
      <div>
        <UiLabel className='label'> {t('content:contentsCount')}</UiLabel>
        <div className='mt-10'>
          <UiTextField
            placeholder={t('content:rowsCount')}
            value={count}
            onChange={changeValue}
            onKeyDown={(e) => {
              if (
                e.key === '.' ||
                e.key === '-' ||
                e.key === '+' ||
                e.key === 'e' ||
                e.key === 'E'
              )
                e.preventDefault();
            }}
            style={{
              width: 250,
            }}
            inputProps={{ min: 1, type: 'number' }}
          ></UiTextField>
          <div>
            <UiLabel
              className='label'
              color='#6C879B'
              fontSize={10}
              marginRight={5}
            >
              {t('content:maxRows', {
                maxRowsCount: commaSeperator(
                  limits?.data?.remaining > maximumExcelRecord()
                    ? maximumExcelRecord()
                    : limits?.data?.remaining
                ),
              })}
            </UiLabel>
          </div>
        </div>
        {randomSort && (
          <div>
            <UiFormControlLabel
              label={t('content:sortContentsRandomly')}
              key='random-sort'
              className='random-sort'
              onChange={changeSortValue}
              checked={isRandomSortEnabled}
              control={<UiCheckbox />}
            ></UiFormControlLabel>
          </div>
        )}
        <div>
          <div className='excel-format-title'>{t('content:fileFormat')}</div>
          <UiRadioGroup
            value={Object.keys(exportFormats)?.find(
              (key) => exportFormats[key].selected
            )}
            className='excel-format-radios'
            onChange={changeFormat}
          >
            {Object.keys(exportFormats)?.map((key) => (
              <UiFormControlLabel
                key={key}
                className='mr-36'
                label={exportFormats[key]?.title}
                control={<UiRadio value={key} />}
              />
            ))}
          </UiRadioGroup>
        </div>
        <div className='mt-8'>
          <UiLabel className='label'>{t('content:fileName')}</UiLabel>
          <div className='mt-10'>
            <UiTextField
              placeholder='content_subject_YY-MM-DD_hh-mm'
              value={title}
              onChange={changeTitle}
              onKeyUp={handleEnter}
              style={{
                width: 360,
                direction: 'ltr',
              }}
            ></UiTextField>
            <div>
              <UiLabel
                className='label'
                color='#6C879B'
                fontSize={10}
                marginRight={5}
              >
                {t('content:excelFileNameRules')}
              </UiLabel>
            </div>
          </div>
        </div>
      </div>
      {!limits?.data?.is_unlimited_access && (
        <div className='export-excel-ring-chart flex col'>
          <RingChart
            data={chartTooltips}
            content={chartContent}
            intervalConfig={{
              color: ['type', ['#0F5E62', '#AADBDD']],
            }}
            annotationConfig={{
              fill: '#000',
              fontWeight: 500,
            }}
          />
          <div className='flex col ring-chart-legend'>
            <span className='legend-title'>
              {t('settings:excelMonthlyRemainingVolume')}
            </span>
            <span className='legend-subtitle'>
              {t('settings:excelRemainingRowsCount', {
                remainingRows: commaSeperator(limits?.data?.remaining),
              })}
            </span>
          </div>
        </div>
      )}
    </StyledExportContent>
  );
};

ExportContentView.propTypes = {
  submit: PropTypes.func,
  randomSort: PropTypes.bool,
};

export default ExportContentView;
